import { Component, OnInit, inject, signal } from '@angular/core';
import { RouterLink } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { TableModule } from 'primeng/table';
import { LocalizationService } from '../../core/localization/localization.service';
import { AinDatePipe } from '../../shared/design-system/ain-date.pipe';
import { AinPageComponent } from '../../shared/design-system/page.component';
import { AinStateComponent } from '../../shared/design-system/state.component';
import { AiSalesModelRecord, SalesAiApiService } from './sales-ai-api.service';

@Component({
  standalone: true,
  selector: 'ain-sales-ai-models',
  imports: [AinPageComponent, AinStateComponent, RouterLink, TableModule, ButtonModule, InputTextModule, FormsModule, AinDatePipe],
  template: `
    <ain-page [title]="t('sales.ai.models.title')" [description]="t('sales.ai.models.description')">
      @if (loading()) { <ain-state type="loading" [title]="t('common.loading')" /> } @else {
        <div class="toolbar">
          <a class="back-link" [routerLink]="['/sales/ai-recommendations']">{{ t('catalog.action.backToList') }}</a>
          <button pButton type="button" [label]="t('sales.action.refresh')" severity="secondary" (click)="load()"></button>
        </div>
        <section class="register card">
          <label><span>{{ t('sales.ai.fields.modelKey') }}</span><input pInputText [(ngModel)]="form.modelKey" /></label>
          <label><span>{{ t('sales.ai.fields.modelVersion') }}</span><input pInputText [(ngModel)]="form.modelVersion" /></label>
          <button pButton type="button" [label]="t('sales.ai.models.register')" (click)="register()"></button>
        </section>
        <p-table [value]="rows()" [paginator]="true" [rows]="15">
          <ng-template pTemplate="header">
            <tr>
              <th>{{ t('sales.ai.fields.modelKey') }}</th>
              <th>{{ t('sales.ai.fields.modelVersion') }}</th>
              <th>{{ t('sales.ai.fields.provider') }}</th>
              <th>{{ t('columns.status') }}</th>
              <th>{{ t('sales.orders.fields.createdAt') }}</th>
            </tr>
          </ng-template>
          <ng-template pTemplate="body" let-row>
            <tr>
              <td>{{ row.modelKey }}</td>
              <td>{{ row.modelVersion }}</td>
              <td>{{ row.provider }}</td>
              <td>{{ row.status }}</td>
              <td>{{ row.createdAtUtc | ainDate:'datetime' }}</td>
            </tr>
          </ng-template>
        </p-table>
      }
    </ain-page>
  `,
  styles: [`
    .toolbar{display:flex;gap:8px;align-items:center;margin-bottom:10px}
    .back-link{color:#1d4ed8;font-weight:600;text-decoration:none}
    .card{border:1px solid #e5e7eb;border-radius:12px;padding:12px;margin-bottom:10px;background:#fff}
    .register{display:grid;grid-template-columns:repeat(3,minmax(0,1fr));gap:10px;align-items:end}
    label{display:grid;gap:4px;font-size:12px}
  `]
})
export class SalesAiModelsComponent implements OnInit {
  private readonly api = inject(SalesAiApiService);
  private readonly localization = inject(LocalizationService);

  readonly loading = signal(true);
  readonly rows = signal<AiSalesModelRecord[]>([]);
  form = { modelKey: 'sales-ai-stub', modelVersion: '1.0.0-stub' };

  ngOnInit(): void { this.load(); }

  load(): void {
    this.loading.set(true);
    this.api.listModels().subscribe({
      next: items => { this.rows.set(items); this.loading.set(false); },
      error: () => { this.rows.set([]); this.loading.set(false); }
    });
  }

  register(): void {
    this.api.registerModel(this.form).subscribe({ next: () => this.load() });
  }

  t(key: string): string { return this.localization.translate(key); }
}
